import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import NumberInput from '@/components/ui/NumberInput';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { TipoMoneda, Cobro, MetodoPago } from '../../types/cobros';
import { DatePicker, dateToCalendarDate, calendarDateToDate } from '@/components/ui/date-picker';
import { METODOS_PAGO_OPTIONS } from '../../constants/config';
import { useCajasContabilidad } from '../../hooks/useCajasContabilidad';

interface ModalRegistrarCobroProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (data: {
    monto: number;
    fecha: string;
    descripcion: string;
    metodoPago?: MetodoPago | null;
    cajaId?: number | null;
  }) => void;
  moneda: TipoMoneda;
  saldoPendiente: number;
  isLoading?: boolean;
  cobroEditar?: Cobro | null;
  montoTotal?: number;
  totalCobradoActual?: number;
}

export const ModalRegistrarCobro: React.FC<ModalRegistrarCobroProps> = ({
  isOpen,
  onClose,
  onConfirm,
  moneda,
  saldoPendiente,
  isLoading = false,
  cobroEditar = null,
  montoTotal = 0,
  totalCobradoActual = 0,
}) => {
  const [monto, setMonto] = useState<string>('');
  const [fecha, setFecha] = useState<Date | null>(new Date());
  const [descripcion, setDescripcion] = useState<string>('');
  const [metodoPago, setMetodoPago] = useState<MetodoPago | null>(null);
  const [cajaId, setCajaId] = useState<number | null>(null);
  const [error, setError] = useState<string>('');

  const { cajas } = useCajasContabilidad();
  // Filtrar cajas por moneda del cobro
  const cajasFiltradas = cajas.filter((c) => c.moneda === moneda);

  const esEdicion = !!cobroEditar;

  // Pre-cargar datos si estamos editando
  useEffect(() => {
    if (cobroEditar) {
      setMonto(cobroEditar.monto.toString());
      // Parse ISO string as local date
      const [year, month, day] = cobroEditar.fecha.split('-').map(Number);
      const fechaDate = new Date(year, month - 1, day);
      setFecha(fechaDate);
      setDescripcion(cobroEditar.descripcion || '');
      setMetodoPago(cobroEditar.metodoPago || null);
      setCajaId(cobroEditar.cajaId ?? null);
    } else {
      // Reset al abrir para crear nuevo
      const today = new Date();
      setFecha(new Date(today.getFullYear(), today.getMonth(), today.getDate()));
      setMonto('');
      setDescripcion('');
      setMetodoPago(null);
      setCajaId(null);
    }
    setError('');
  }, [cobroEditar, isOpen]);

  const handleConfirm = async () => {
    setError('');

    // Validar que el monto no esté vacío
    if (!monto || monto.trim() === '') {
      setError('El monto es obligatorio');
      return;
    }

    const montoNumerico = parseFloat(monto);

    // Validar que sea un número válido
    if (isNaN(montoNumerico)) {
      setError('Ingrese un monto válido');
      return;
    }

    // Validar que sea mayor a cero
    if (montoNumerico <= 0) {
      setError('El monto debe ser mayor a cero');
      return;
    }

    // Validar que no exceda el total
    if (esEdicion) {
      // Al editar, calcular el nuevo total excluyendo el cobro actual
      const totalSinCobroActual = totalCobradoActual - (cobroEditar?.monto || 0);
      const nuevoTotal = totalSinCobroActual + montoNumerico;

      if (nuevoTotal > montoTotal) {
        const disponible = montoTotal - totalSinCobroActual;
        setError(`El monto excede el total. Disponible: $${disponible.toFixed(2)}`);
        return;
      }
    } else {
      // Al crear, validar contra el saldo pendiente
      if (montoNumerico > saldoPendiente) {
        setError(`El monto del cobro excede el saldo pendiente ($${saldoPendiente.toFixed(2)})`);
        return;
      }
    }

    // Validar fecha
    if (!fecha) {
      setError('La fecha es obligatoria');
      return;
    }

    try {
      // Convert Date to ISO string for API using local time
      const year = fecha.getFullYear();
      const month = String(fecha.getMonth() + 1).padStart(2, '0');
      const day = String(fecha.getDate()).padStart(2, '0');
      const fechaISO = `${year}-${month}-${day}`;

      await onConfirm({
        monto: montoNumerico,
        fecha: fechaISO,
        descripcion: descripcion.trim(),
        metodoPago: metodoPago,
        cajaId: cajaId,
      });
      handleClose();
    } catch (err) {
      setError('Error al registrar el cobro. Intente nuevamente.');
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setMonto('');
      const today = new Date();
      setFecha(new Date(today.getFullYear(), today.getMonth(), today.getDate()));
      setDescripcion('');
      setMetodoPago(null);
      setCajaId(null);
      setError('');
      onClose();
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open && !isLoading) {
      handleClose();
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && e.target instanceof HTMLInputElement) {
      handleConfirm();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {esEdicion ? 'Editar Cobro' : 'Registrar Cobro'} - {moneda}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="monto-cobro">
              Monto del cobro *
            </Label>
            <NumberInput
              id="monto-cobro"
              currency={true}
              decimals={2}
              min={0}
              value={monto}
              onChange={(value) => {
                setMonto(value);
                setError('');
              }}
              onKeyDown={handleKeyPress}
              placeholder="Ingrese el monto del cobro"
              autoFocus
              disabled={isLoading}
              aria-invalid={!!error}
            />
            {!esEdicion && saldoPendiente > 0 && (
              <p className="text-xs text-muted-foreground">
                Saldo pendiente: ${saldoPendiente.toFixed(2)}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="metodo-pago">
              Método de pago <span className="text-muted-foreground font-normal">(opcional)</span>
            </Label>
            <Select
              value={metodoPago || ''}
              onValueChange={(value) => setMetodoPago(value as MetodoPago || null)}
              disabled={isLoading}
            >
              <SelectTrigger id="metodo-pago" className="w-full">
                <SelectValue placeholder="Seleccionar método de pago" />
              </SelectTrigger>
              <SelectContent>
                {METODOS_PAGO_OPTIONS.map((metodo) => (
                  <SelectItem key={metodo} value={metodo}>
                    {metodo}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {cajasFiltradas.length > 0 && (
            <div className="space-y-2">
              <Label htmlFor="caja-cobro">
                Caja destino <span className="text-muted-foreground font-normal">(opcional)</span>
              </Label>
              <Select
                value={cajaId ? String(cajaId) : ''}
                onValueChange={(value) => setCajaId(value ? Number(value) : null)}
                disabled={isLoading}
              >
                <SelectTrigger id="caja-cobro" className="w-full">
                  <SelectValue placeholder="Seleccionar caja destino" />
                </SelectTrigger>
                <SelectContent>
                  {cajasFiltradas.map((caja) => (
                    <SelectItem key={caja.id} value={String(caja.id)}>
                      {caja.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="fecha-cobro">
              Fecha de cobro *
            </Label>
            <DatePicker
              value={dateToCalendarDate(fecha)}
              aria-label="Fecha de cobro"
              onChange={(calendarDate) => {
                const date = calendarDateToDate(calendarDate);
                setFecha(date);
                setError('');
              }}
              placeholder="Seleccionar fecha"
              disabled={isLoading}
              inModal={true}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="descripcion-cobro">
              Descripción <span className="text-muted-foreground font-normal">(opcional)</span>
            </Label>
            <Textarea
              id="descripcion-cobro"
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              placeholder="Ej: Seña inicial, Segundo pago, etc."
              rows={3}
              disabled={isLoading}
            />
          </div>

          {error && (
            <div className="rounded-md bg-destructive/10 p-3 border border-destructive/20">
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            loading={isLoading}
          >
            Confirmar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
